"use client";

import { useRef } from "react";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import { downloadSvg } from "@/lib/tool-kit/download";
import { CHART_INK, seriesColor } from "@/lib/tool-kit/palette";
import type { NumberFormatter } from "@/lib/tool-kit/stats-tests/format";

/*
 * A profile (interaction) plot: one line per level of the trace factor, across the
 * levels of the x-axis factor, through the cell means. Parallel lines mean no
 * interaction; crossing or diverging lines are what an interaction looks like. Drawn on
 * white with the Okabe–Ito palette so the downloaded SVG reads the same in a paper.
 */

const WIDTH = 600;
const HEIGHT = 340;
const MARGIN = { top: 20, right: 150, bottom: 56, left: 64 };
const PLOT_W = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_H = HEIGHT - MARGIN.top - MARGIN.bottom;
const TICKS = 5;

/** Round tick steps: 1, 2 or 5 × 10ᵏ. */
export function niceTicks(min: number, max: number, count = TICKS): number[] {
  const span = max - min || Math.abs(max) || 1;
  const raw = span / count;
  const power = 10 ** Math.floor(Math.log10(raw));
  const factor = raw / power;
  let step = 10 * power;
  if (factor <= 1) {
    step = power;
  } else if (factor <= 2) {
    step = 2 * power;
  } else if (factor <= 5) {
    step = 5 * power;
  }
  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= end + step / 2; v += step) {
    ticks.push(Number(v.toPrecision(12)));
  }
  return ticks;
}

export function InteractionPlot({
  xLevels,
  traceLevels,
  means,
  xTitle,
  yTitle,
  label,
  downloadLabel,
  fmt,
}: {
  xLevels: readonly string[];
  traceLevels: readonly string[];
  /** means[trace][x]. */
  means: readonly (readonly number[])[];
  xTitle: string;
  yTitle: string;
  label: string;
  downloadLabel: string;
  fmt: NumberFormatter;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const all = means.flat();
  const ticks = niceTicks(Math.min(...all), Math.max(...all));
  const yMin = ticks[0];
  const yMax = ticks.at(-1) as number;
  const y = (v: number) =>
    MARGIN.top + PLOT_H - ((v - yMin) / (yMax - yMin || 1)) * PLOT_H;
  const x = (i: number) =>
    MARGIN.left +
    (xLevels.length === 1 ? PLOT_W / 2 : (i / (xLevels.length - 1)) * PLOT_W);
  return (
    <div className="space-y-2">
      <svg
        aria-label={label}
        className="h-auto w-full max-w-2xl rounded-lg border border-border"
        ref={ref}
        role="img"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect fill={CHART_INK.background} height={HEIGHT} width={WIDTH} />
        {ticks.map((tick) => (
          <g key={`tick-${tick}`}>
            <line
              stroke={CHART_INK.grid}
              x1={MARGIN.left}
              x2={MARGIN.left + PLOT_W}
              y1={y(tick)}
              y2={y(tick)}
            />
            <text
              dominantBaseline="middle"
              fill={CHART_INK.muted}
              fontSize="12"
              textAnchor="end"
              x={MARGIN.left - 8}
              y={y(tick)}
            >
              {fmt(tick)}
            </text>
          </g>
        ))}
        <line
          stroke={CHART_INK.axis}
          x1={MARGIN.left}
          x2={MARGIN.left + PLOT_W}
          y1={MARGIN.top + PLOT_H}
          y2={MARGIN.top + PLOT_H}
        />
        {xLevels.map((level, i) => (
          <text
            fill={CHART_INK.text}
            fontSize="12"
            key={`x-${level}`}
            textAnchor="middle"
            x={x(i)}
            y={MARGIN.top + PLOT_H + 20}
          >
            {level}
          </text>
        ))}
        <text
          fill={CHART_INK.text}
          fontSize="13"
          textAnchor="middle"
          x={MARGIN.left + PLOT_W / 2}
          y={HEIGHT - 10}
        >
          {xTitle}
        </text>
        <text
          fill={CHART_INK.text}
          fontSize="13"
          textAnchor="middle"
          transform={`translate(16 ${MARGIN.top + PLOT_H / 2}) rotate(-90)`}
        >
          {yTitle}
        </text>
        {means.map((row, s) => {
          const color = seriesColor(s);
          const points = row.map((v, i) => `${x(i)},${y(v)}`).join(" ");
          return (
            <g key={`series-${traceLevels[s]}`}>
              <polyline
                fill="none"
                points={points}
                stroke={color}
                strokeWidth="2.5"
              />
              {row.map((v, i) => (
                <circle
                  cx={x(i)}
                  cy={y(v)}
                  fill={color}
                  key={`pt-${traceLevels[s]}-${xLevels[i]}`}
                  r="4.5"
                />
              ))}
              <rect
                fill={color}
                height="10"
                width="18"
                x={MARGIN.left + PLOT_W + 20}
                y={MARGIN.top + s * 22}
              />
              <text
                dominantBaseline="middle"
                fill={CHART_INK.text}
                fontSize="12"
                x={MARGIN.left + PLOT_W + 44}
                y={MARGIN.top + s * 22 + 5}
              >
                {traceLevels[s]}
              </text>
            </g>
          );
        })}
      </svg>
      <button
        className={BUTTON_SMALL}
        onClick={() => {
          if (ref.current) {
            downloadSvg("interaction-plot.svg", ref.current);
          }
        }}
        type="button"
      >
        {downloadLabel}
      </button>
    </div>
  );
}
